import React from "react";
import { flagsAndExperimentNames, useOnLoadFlagOrExperiment } from "./with-flags-and-experiments";
import PersistedFilters from "helpers/persisted-filters-helper";
import type { WorkingFilters } from "components/types/working-filters";

export const usePersistedFilters = () => {
  const [reserveApiFlag] = useOnLoadFlagOrExperiment(flagsAndExperimentNames.ENABLE_RESERVE_FROM_NEXT_GRIDS);
  const flagAsBool = Boolean(reserveApiFlag);

  const { writePreference, writePreferences, clearPreference, clearPreferences } = new PersistedFilters(flagAsBool);

  return { writePreference, writePreferences, clearPreference, clearPreferences };
};

type PersistedFilterProps = {
  writePreference: <T extends keyof WorkingFilters>(key: T, value: WorkingFilters[T]) => void;
  writePreferences: (updatedFilters: WorkingFilters) => void;
  clearPreference: (key: keyof WorkingFilters) => void;
  clearPreferences: (keys: (keyof WorkingFilters)[]) => void;
};

/**
 * sbenedict 6/25
 * Provides methods for saving filters to local/session storage or cookies as necessary.
 * In an ideal world, we'd be doing this inside the same Redux action that updates workingFilters (where we also have access to flags).
 * Unfortunately, we did not do this historically, and we have multiple different ways of setting workingFilters in Redux.
 * I don't want to make an already large refactor larger, so for now separating this functionality out as before.
 */
export function withPersistedFilters<P>(Component: React.ComponentType<P & PersistedFilterProps>) {
  const WrappedComponent = (props: P) => {
    const { writePreference, writePreferences, clearPreference, clearPreferences } = usePersistedFilters();

    return (
      <Component
        writePreference={writePreference}
        writePreferences={writePreferences}
        clearPreference={clearPreference}
        clearPreferences={clearPreferences}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    );
  };

  return WrappedComponent;
}
